import React, { useState, useEffect, useRef } from "react";
import { sms, transactionAPI, accountAPI } from "services";
import { formatMoneyInput } from "utils";
import { creatableStyle } from "assets/themes";
import txnaudio from "assets/sounds/google_pay-mini.mp3";
import FilePicker from "views/FilePicker";
import Creatable from "react-select/creatable";
import useUniversalStore from "store/universalStore";
import DatePicker from "react-datepicker";
import { toast } from "sonner";
import useOffcanvasStore from "store/offcanvasStore";
import { Controller, useForm, useWatch } from "react-hook-form";
import { defaultValues, validateAmount, validateNotify, validateAccountName } from "./config";
import { stringSort } from "utils";
import { trackEvent } from "lib/analytics";

const amountRules = {
  required: "Amount is required",
  validate: validateAmount
};

const nameRules = {
  maxLength: { value: 50, message: "Name cannot exceed 50 characters" },
  validate: validateAccountName
};

const OneTimeTxn = () => {
  const { accounts, accountId, userBasic, write, user } = useUniversalStore((state) => state);
  const { closeOffcanvas } = useOffcanvasStore();
  const { control, handleSubmit, setValue, register, formState: { errors, isSubmitting, isValid } } = useForm({
    defaultValues,
    mode: "onBlur"
  });

  // const isSubmitting = true;
  const filePickerRef = useRef(null);

  const accountName = useWatch({ control, name: "accountName" });
  const phone = useWatch({ control, name: "phone" });
  const attachment = useWatch({ control, name: "attachment" });
  const notify = useWatch({ control, name: "notify" });
  const isCash = useWatch({ control, name: "isCash" });

  const [isKHATA, setIsKHATA] = useState(true);
  const [multiplier, setMultiplier] = useState(1);
  const [accountOptions, setAccountOptions] = useState([]);
  const [btnLabel, setBtnLabel] = useState({
    "in": "Cash In",
    "out": "Cash Out"
  });

  useEffect(() => {
    trackEvent("Add Transaction - Started");
    setValue("date", new Date());
  }, []);

  useEffect(() => {
    if (!phone || phone === "+91") setValue("notify", false);
    else setValue("notify", notify);
  }, [phone]);

  useEffect(() => {
    if (accountId) {
      const account = accounts.find((acc) => acc.id === accountId);
      if (account) {
        setValue("accountName", account.get("name"));
        setValue("phone", account.get("phone"));
      }
      setIsKHATA(true);
      setValue("notify", account.get("checkboxSMS"));
    }
    if (window.location.pathname === "/") {
      setIsKHATA(false);
      setValue("accountName", "");
      setValue("phone", "");
      setValue("notify", false);
    }
  }, [accountId, accounts]);

  useEffect(() => {
    const filtered = accounts?.filter((a) => !a.get("cancelled"));
    const sorted = stringSort(filtered, false, "name");
    setAccountOptions(sorted.map((a) => ({ value: a.id, label: a.get("name"), checkboxSMS: a.get("checkboxSMS"), phone: a.get("phone") })));
  }, [accounts]);


  const onAmountChange = (e, onChange) => {
    const rawValue = e.target.value.replace(/[^0-9.]/g, "");
    const parts = rawValue.split('.');
    let cleanedValue = parts[0];
    if (parts.length > 1) {
      cleanedValue += '.' + parts[1].substring(0, 2);
    }

    if (cleanedValue === "") {
      onChange(0);
      return;
    }

    onChange(cleanedValue);
  };

  const onAccountChange = (selectedOption) => {
    if (!selectedOption) {
      setValue("accountName", "");
      setValue("phone", "");
      return;
    }
    setValue("accountName", selectedOption.label, { shouldValidate: true });
    setValue("phone", selectedOption.__isNew__ ? null : selectedOption.phone);
    setValue("notify", selectedOption.checkboxSMS);
  };

  const onNotifyChange = () => {
    if (!notify) {
      if (!accountName) {
        toast.error("Select an account to notify");
        return;
      }
      if (!phone || phone?.length < 10) {
        toast.error("Account does not have a phone number");
        return;
      }
    }
    setValue("notify", !notify);
  };

  const onIsCashChange = () => {
    const newIsCash = !isCash;
    setValue("isCash", newIsCash);
    setBtnLabel(newIsCash ? {
      "in": "Cash In",
      "out": "Cash Out"
    } : {
      "in": "You Received",
      "out": "You Gave"
    });
  };

  const onSubmit = async (data) => {
    try {
      const result = await transactionAPI.transaction(
        data.accountName,
        data.amount * multiplier,
        data.detail,
        data.attachment?.url,
        data.date,
        data.isCash
      );

      if (result) {
        toast.success("Transaction done successfully.");
        closeOffcanvas();
        new Audio(txnaudio).play();
      }

      //Send SMS notification on successful transaction
      const partyPhone = result?.get("accountId")?.get("phone");
      if (partyPhone && notify) {
        const countryCode = result?.get("accountId")?.get("phone").startsWith("+91") ? "" : "+91";
        const amount = result.get("amount");
        const partyName = userBasic?.business?.name || user.get("username");

        if (amount < 0) {
          sms.sendPaidSMS(Math.abs(amount), partyName, result.get("accountId").id, countryCode + partyPhone);
        } else {
          sms.sendReceiveSMS(Math.abs(amount), partyName, result.get("accountId").id, countryCode + partyPhone);
        }
      }

      const transactionDate = new Date(data.date);
      write({ selectedMonth: transactionDate });
      if (accountId && window.location.pathname === "/khatabook") {
        write({
          transactions: await transactionAPI.getTransactionsByAccountIdWithMonth(accountId, transactionDate),
          accounts: await accountAPI.getAccounts(),
        });
        trackEvent("Add Transaction - Done");
      } else {
        write({
          transactions: await transactionAPI.getTransactionsByDate(transactionDate),
          accounts: await accountAPI.getAccounts(),
        });
        trackEvent("Add Transaction - Done");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const inputClass = (name) => `form-control ${errors[name] ? "is-invalid" : ""}`;
  const labelStyle = { fontSize: "0.75rem" };

  return (
    <form onSubmit={handleSubmit(onSubmit)} id="newtxnform" className="row p-0">

      <div className="mb-2 mb-md-3 col-6">
        <Controller
          name="amount"
          control={control}
          rules={amountRules}
          render={({ field: { value, onChange, ...rest } }) => (
            <div className="form-control border-0 p-0">
              <label className="text-app" style={labelStyle}>Amount</label>
              <input
                {...rest}
                type="text"
                placeholder="Amount"
                className={inputClass("amount")}
                value={formatMoneyInput(value)}
                onChange={(e) => onAmountChange(e, onChange)}
                disabled={isSubmitting}
              />
            </div>
          )}
        />
        {errors.amount && <div className="invalid-feedback d-block">{errors.amount.message}</div>}
      </div>

      <div className="mb-2 mb-md-3 col-6">
        <Controller
          name="date"
          control={control}
          rules={{ required: "Date is required" }}
          render={({ field: { value, onChange } }) => (
            <div className="form-control border-0 p-0">
              <label className="text-app" style={labelStyle}>Date</label>
              <DatePicker
                selected={value}
                onChange={onChange}
                dateFormat="dd-MM-yyyy"
                maxDate={new Date()}
                popperPlacement="bottom-start"
                className="form-control"
                wrapperClassName="w-100"
                disabled={isSubmitting}
              />
            </div>
          )}
        />
        {errors.date && <div className="invalid-feedback d-block">{errors.date.message}</div>}
      </div>

      {!isKHATA && (
        <div className="mb-2 mb-md-3">
          <Controller
            name="accountName"
            control={control}
            rules={nameRules}
            render={({ field: { value } }) => (
              <Creatable
                options={accountOptions}
                value={value ? { label: value, value: phone } : null}
                classNamePrefix="rs"
                isClearable
                placeholder="Account Name"
                onChange={onAccountChange}
                classNames={creatableStyle}
                className={errors.accountName ? "is-invalid" : value ? "is-valid" : ""}
                isDisabled={isSubmitting}
              />
            )}
          />
          {errors.accountName && <div className="invalid-feedback d-block">{errors.accountName.message}</div>}
        </div>
      )}

      <div className="mb-3">
        <div className="border-0 p-0">
          <label className="text-app" style={labelStyle}>Detail</label>
          <div className="form-control d-flex align-items-end p-0">
            {/* <textarea
              {...register("detail")}
              placeholder="Details"
              className="form-control border-0 p-0 shadow-none"
              rows={2}
              autoComplete="on"
              maxLength={50}
              disabled={isSubmitting}
            /> */}
            <input
              type="text"
              {...register("detail")}
              placeholder="Details"
              autoComplete="on"
              maxLength={50}
              disabled={isSubmitting}
              className="form-control border-0 shadow-none"
            />
            <button
              type="button"
              className="btn text-app mb-1"
              onClick={() => filePickerRef.current?.click()}
              title="Attach File"
              disabled={isSubmitting}
            >
              <i className="bi bi-paperclip fs-4"></i>
            </button>
          </div>
        </div>
        {errors.detail && <div className="invalid-feedback d-block">{errors.detail.message}</div>}
      </div>

      <div className="mb-5 pb-5 px-2">
        <FilePicker
          ref={filePickerRef}
          onPick={(field, value) => setValue(field, value, { shouldValidate: true })}
          loading={attachment?.loading}
        />
      </div>

      <div className="position-absolute bottom-0 start-0 end-0">
        <div className="row mb-2">
          <div className="col-6 col-md-6">
            {/* Option to post only in Ledger and not Cashbook should be shown only in Khata */}
            {isKHATA && (
              <div className="mb-3">
                <Controller
                  name="isCash"
                  control={control}
                  render={({ field: { value } }) => (
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input p-2"
                        role="button"
                        type="checkbox"
                        checked={value}
                        onChange={onIsCashChange}
                        id="isCashCheckbox"
                      />
                      <label className="form-check-label px-2" htmlFor="isCashCheckbox">
                        Add to Cashbook
                      </label>
                    </div>
                  )}
                />
              </div>
            )}
          </div>
          <div className="col-6 col-md-6">
            <div className="d-flex align-items-center justify-content-end">
              <Controller
                name="notify"
                control={control}
                rules={{ validate: (value) => validateNotify(value, accountName, phone) }}
                render={({ field: { value } }) => (
                  <div className="form-check form-switch m-0 d-flex align-items-center">
                    <label className="form-check-label px-2 mb-0" htmlFor="sendSMSCheckbox">
                      SMS Notification
                    </label>
                    <input
                      className="form-check-input p-2 ms-2"
                      role="button"
                      type="checkbox"
                      checked={value}
                      onChange={onNotifyChange}
                      id="sendSMSCheckbox"
                    />
                  </div>
                )}
              />
            </div>
            {errors.notify && (
              <div className="invalid-feedback d-block text-end">
                <i className="bi bi-patch-exclamation-fill me-2" />
                {errors.notify.message}
              </div>
            )}
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-6 d-grid mt-md-0">
            <button
              type="submit"
              className="btn btn-success block py-3"
              disabled={attachment?.loading || isSubmitting || !isValid}
              onClick={() => setMultiplier(1)}>
              <b className="fs-6 fs-md-5 text-uppercase"><i className="fs-4 bi bi-arrow-down-circle-fill"></i> {btnLabel.in}</b>
            </button>
          </div>
          <div className="col-6 d-grid mt-md-0">
            <button
              type="submit"
              className="btn btn-danger block py-3"
              disabled={attachment?.loading || isSubmitting || !isValid}
              onClick={() => setMultiplier(-1)}>
              <b className="fs-6 fs-md-5 text-uppercase"><i className="fs-4 bi bi-arrow-up-circle-fill"></i> {btnLabel.out}</b>
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default OneTimeTxn;
